/**
 * Framework générique de {House} gérant l'échange de {Msg} entre ces {House} au travers de {Road}.
 */

/** Options de création d'une Locality. */
export interface OLocalityConfig {
	/**
	 * Identifiant unique dans tout le réseau des localités interconnectées.
	 * Utilisé par les échanges de Msg.
	 */
	id?: string;

	/* Localité parente. */
	//superLocality?: Locality;
}

/** Options de création d'une Door. */
export interface ODoorConfig {
	/* Si true, cette house est le master de la house de l'autre extrémité de la road.*/
	isMaster?: boolean;
	/* Si true, door vers le master de cette house.*/
	roadToMaster?: boolean;
	/* Si true et que roadToMaster==true, initialisation automatique via this.initFromMaster();*/
	autoInitFromMaster?: boolean;
}

/**
 */
abstract class Locality {
	public id: string;
	//public superLocality: Locality;
	//public address: string;

	/**
	 *
	 * @param options
	 *  - name : Nom de cette Locality (nom unique dans le scope de sa superLocality)
	 *  - superLocality : Locality parente
	 *  - id : identifiant unique dans tout le réseau des localités interconnectées
	 */
	constructor(options: OLocalityConfig = {}) {
		this.id = options.id;
		//this.superLocality = options.superLocality;
		//this.address = this.xBuildAddress([]).join("/");
	}


	/**
	 * Id : identifiant unique dans tout le réseau des localités interconnectées.
	 * Utilisé par les échanges de Msg.
	 * Cet id peut être initialisé via le contexte parent d'instanciation via OLocalityConfig ou
	 * via le 1er échange avec une house de type master vis à vis de cette house.
	 */
	setId(id: string) {
		if (this.id) throw "Id already setted : " + this.id;
		this.id = id;
		return this;
	}

	toString() {
		return this.id; // ? this.address + "#" + this.id : this.address;
	}

	// xBuildAddress(addr: Array<string>, from?: Locality): Array<string> {
	// 	if (from === this) return;
	// 	if (this.superLocality) this.superLocality.xBuildAddress(addr, from);
	// 	addr.push(this.name);
	// 	return addr;
	// }

}

/**
 * Une IFactory permet d'instancier un objet à partir d'un forme JSON ou Struct
 * (https://developer.mozilla.org/en-US/docs/Web/API/Web_Workers_API/Structured_clone_algorithm,
 * http://w3c.github.io/html/infrastructure.html#safe-passing-of-structured-data).
 */
interface IFactory<T> {

	/** String unique identifiant le type de T. */
	type: string;

	/**
	 * @param json Doit posseder une propriété "type" correspondant à la factory.
	 * @param factoryReg Registre à utiliser pour créer des sous-objets.
	 */
	buildFromJson(json: any, factoryReg: FactoryRegistry<T>): T;

	/**
	 * @param json Doit posseder une propriété "type" correspondant à la factory.
	 * @param factoryReg Registre à utiliser pour créer des sous-objets.
	 */
	buildFromStruct(struct: any, factoryReg: FactoryRegistry<T>): T;

}


/**
 * Registre de IFactory d'objets (Msg, State).
 */
export class FactoryRegistry<T> {
	protected name: string;
	protected _parent: FactoryRegistry<T>;
	protected _msgFactories: Dict<IFactory<T>>;

	constructor(name: string, parent?: FactoryRegistry<T>) {
		this.name = name;
		this._parent = parent;
		this._msgFactories = {};
	}

	register(factory: IFactory<T>) {
		this._msgFactories[factory.type] = factory;
	}

	getFactory(type: string): IFactory<T> {
		let factory = this._msgFactories[type];
		if (!factory) {
			if (this._parent) factory = this._parent.getFactory(type);
			if (!factory) console.log("Type '" + type + "' not found in FactoryRegistry " + this.name);
		}
		return factory;
	}

	newFromJson(json: any): T {
		return this.getFactory(json.type).buildFromJson(json, this);
	}

	newFromStruct(struct: any): T {
		return this.getFactory(struct.type).buildFromJson(struct, this);
	}
}

/**
 * Une house est la localité terminale.
 * Un house peut contenir 3 types de doors :
 * - 0-1 door connecté à la road vers sa house master
 * - 0-N doors connectées à une road vers ses house slaves
 * - 0-N doors connectées à une road vers d'autres houses (sans relation master/slave)
 * - 1 door "publique" utilisable de l'extérieur du réseau connecté des roads / houses.
 *
 */
export abstract class House extends Locality {

	static DEBUG = false;
	static ASSERT = false;
	static MSG_FACTORYREG = new FactoryRegistry<Msg>("Msg");
	static STATE_FACTORYREG = new FactoryRegistry<State>("State");

	protected _masterDoor: Door;
	protected _doors: Array<Door> = [];
	protected _publicDoors: Array<PublicDoor> = [];
	protected _doorCounter = 0;
	protected _slaveIdCounter: number;

	constructor(options: OLocalityConfig) {
		super(options);
	}

	abstract getStateById<T extends State>(id: string): T

	openDoor(roadEnd: RoadEnd, doorConfig: ODoorConfig): Door {
		return this.xDeclareDoor(new Door(this, ++this._doorCounter, roadEnd, doorConfig), doorConfig);
	}

	/* @intern */
	protected xDeclareDoor(newDoor: Door, doorConfig: ODoorConfig): Door {
		if (doorConfig.roadToMaster) {
			if (this._masterDoor) throw new Error("Master door already setted");
			this._masterDoor = newDoor;
			if (doorConfig.autoInitFromMaster) this.initFromMaster();
		} else {
			this._doors.push(newDoor);
		}
		return newDoor;
	}

	/**
	 * Clos une door.
	 * Si c'est une door publique, on considère que l'appel à cette méthode est issu du contexte public.
	 * Si c'est une door inter-houses, on considère que l'appel à cette méthode est issu de la road.
	 */
	closeDoor(door: IDoorBase): boolean {
		if (door === this._masterDoor) {
			this._masterDoor = null;
			this.broadcastMsgToPublicDoors(new ErrorMsg().init("masterDoorClosed"));
			return true;
		}
		if (door.isPublic()) {
			const idx = this._publicDoors.indexOf(door as any);
			if (idx < 0) return false;
			this._publicDoors.splice(idx, 1);
		} else {
			const idx = this._doors.indexOf(door as any);
			if (idx < 0) return false;
			this._doors.splice(idx, 1);
		}
		return true;
	}

	killHouse() {
		//on clos les door publiques, puis inter-houses, puis le lien au master.
		this.broadcastMsgToPublicDoors(new ErrorMsg().init("houseKilled"));
		this._publicDoors.length = 0;
		this._doors.forEach((d) => d.closeFromHouse());
		this._doors.length = 0;
		if (this._masterDoor) this._masterDoor.closeFromHouse();
		this._masterDoor = null;
		//todo informer les states de la house ?
	}

	/** Crée une nouvelle IPublicDoor. */
	openPublicDoor(): IPublicDoor {
		const door = new PublicDoor(this, -(++this._doorCounter));
		this._publicDoors.push(door);
		//console.log("Public doors::", this._publicDoors.length);
		return door;
	}

	/** publicDoor par défaut. */
	get publicDoor(): IPublicDoor {
		if (this._publicDoors.length == 0) return this.openPublicDoor();
		return this._publicDoors[0];
	}

	get publicDoors(): IPublicDoor[] {return this._publicDoors}

	/** A partir d'un id de door permet de savoir si la door est publique ou non. */
	isDoorPublic(doorId: number) {return doorId < 0}

	get masterDoor() {return this._masterDoor}

	get otherDoors() {return this._doors}

	/** Une house master-root est celle qui n'a plus de master au dessus d'elle. */
	isMasterRoot() {return !this._masterDoor}

	/** Réception d'un msg d'une door. */
	abstract onMsg(msg: Msg, fromDoor: IDoorBase): void;

	/** Réception d'une requete d'une door publique. */
	abstract onRequest<R extends IReply>(msg: IRequest<R>, fromDoor: IPublicDoor): Promise<R>;

	/**
	 * Envoi d'un Msg à travers un ensemble de doors
	 * @param doorFilter Function retournant un booléen appelé avec la door à évaluer en tant que this.
	 */
	broadcastMsg(msg: Msg, exceptDoorId: number = 0, doorFilter?: IDoorFilter) {
		function dispatch(door: IDoorBase) {
			if (!door || door.id === exceptDoorId) return;
			if (doorFilter && doorFilter(door) === false) return;
			try {door.sendMsg(msg)} catch (e) {console.trace(e)}
		}

		this._publicDoors.forEach(dispatch);
		dispatch(this._masterDoor);
		this._doors.forEach(dispatch);
	}

	/** Envoi un msg à toutes les publicDoors (y compris la door d'origine de msg : msg.doorId)*/
	broadcastMsgToPublicDoors(msg: Msg) {
		for (const door of this._publicDoors) try {door.sendMsg(msg)} catch (e) {console.trace(e)}
	}

	/**
	 * Si cette house est un slave vis à vis d'une autre house master,
	 * permet d'initialiser ou réinitialiser ce slave par son master.
	 *
	 * return this.masterDoor.sendRequest(new InitSlaveReq()).then( initSlaveRep =>{
	 *   //init states...
	 * }
	 */
	abstract initFromMaster(): Promise<this>;

	/** Export de l'ensemble des states pour l'init d'un slave, une sauvegarde externe... */
	abstract writeFullStates(datas: any, options?: any): void;

	/** Sur une erreur, reinit */
	onInternalError(e: Error) {
		console.log("Reinit house on internal error", e);
		if (this.isMasterRoot()) {
			const datas = {};
			this.writeFullStates(datas);
			this.broadcastMsg(new ResetStatesMsg().init(datas));
		} else {
			this.initFromMaster();
		}
	}

	/** Générateur d'id unique pour les houses slave de ce master. */
	newSlaveId(): number {
		if (!this._slaveIdCounter) this._slaveIdCounter = 0;
		return ++this._slaveIdCounter;
	}
}


export interface IDoorBase {
	house: House;
	id: number;

	isDoorToSlave: boolean;
	isDoorToMaster: boolean;

	isPublic(): this is IPublicDoor;

	/** Envoi un message simple à l'extérieur de la house. */
	sendMsg(msg: Msg): void;
}

/** Méthodes des publicDoors utilisables en dehors de la house */
export interface IPublicDoor extends IDoorBase {

	/** Listener des msg publiés par la house dans cet publicDoor. */
	addMsgListener(listener: (msg: Msg) => void): void;

	removeMsgListener(listener: (msg: Msg) => void): void;

	/** Reçoit un message de l'extérieur et le transmet à la house. */
	receiveMsg(msg: Msg): void;

	/** Reçoit une requête de l'extérieur et le transmet à la house. */
	receiveRequest<R extends IReply>(msg: IRequest<R>): Promise<R>;

	/** Dispatch un msg aux listeners de cette publicDoor (sans passer par la house) */
	dispatchMsg(msg: Msg): void;
}

/** Méthodes de toutes les doors (public et inter-houses) utilisables par la house et les Roads. */
export interface IDoor extends IDoorBase {

	/** Envoie une request. Un Promise[resolve(Reply), reject(Reply)] est retourné. */
	sendRequest<R extends IReply>(msg: IRequest<R>): Promise<R>;

	/** Reçoit un message de la Road et le transmet à la house. */
	receiveMsg(msg: Msg): void;
}

interface IDoorFilter {
	(door: IDoorBase): boolean;
}

/** Porte d'une house permettant une connexion vers une porte d'une autre house via une road. */
export class Door implements IDoor {

	static FILTER_NOT_PUBLIC: IDoorFilter = function (door: IDoor) {return !door.isPublic()};

	//static FILTER_TO_SLAVE: IDoorFilter = function (door: IDoor) {return door.isDoorToSlave};

	house: House;

	/** Id unique de la door dans sa house. */
	id: number;

	/** La house de cette door est le master de la house à l'autre extrémité de la road. */
	isDoorToSlave: boolean;

	/** La house de cette door est un slave de la house à l'autre extrémité de la road. */
	get isDoorToMaster() {return this.house.masterDoor === this}

	roadEnd: RoadEnd;

	constructor(house: House, doorId: number, roadEnd: RoadEnd, configDoor: ODoorConfig) {
		this.house = house;
		this.id = doorId;
		this.roadEnd = roadEnd;
		if (configDoor && configDoor.isMaster) this.isDoorToSlave = true;
	}

	isPublic(): this is IPublicDoor {return false}

	/** Envoi un message simple à l'extérieur de la house. */
	sendMsg(msg: Msg) {
		this.roadEnd.pushMsg(msg);
	}

	/** Envoie une request. Un Promise[resolve(Reply), reject(Reply)] est retourné. */
	sendRequest<R extends IReply>(msg: IRequest<R>): Promise<R> {
		return this.roadEnd.pushRequest(msg);
	}

	receiveMsg(msg: Msg) {
		if (House.DEBUG) console.group(`Receive msg in ${this.house} : ${msg}`);
		msg.setDoorId(this.id);
		this.house.onMsg(msg, this);
		if (House.DEBUG) console.groupEnd();
	}

	/**
	 * Fermeture initiée par la house.
	 * Permet de transmettre l'info de cette fermeture à la road qui le transmettra à l'autre house...
	 */
	closeFromHouse() {
		this.roadEnd.doorClosed();
	}

	toString() {
		return "Door " + this.house + "." + this.id;
	}
}

/** Porte "publique", sans road connectée, pour permettre à la house de recevoir des messages directement. */
export class PublicDoor implements IPublicDoor {

	house: House;
	id: number;
	_listeners: Set<(msg: Msg) => void>;

	constructor(house: House, doorId: number) {
		this.house = house;
		this.id = doorId;
		this._listeners = new Set();
	}

	//api Door
	get isDoorToSlave() {return false}

	get isDoorToMaster() {return false}

	isPublic(): this is PublicDoor {return true}

	/** Listener des msg publiés par la house dans cet publicDoor. */
	addMsgListener(listener: (msg: Msg) => void) {
		this._listeners.add(listener);
	}

	removeMsgListener(listener: (msg: Msg) => void) {
		this._listeners.delete(listener);
	}

	sendMsg(msg: Msg) {
		this._listeners.forEach(l => {l(msg)});
	}

	dispatchMsg(msg: Msg) {
		this._listeners.forEach(l => {l(msg)});
	}

	receiveMsg(msg: Msg) {
		if (House.DEBUG) console.group(`Public msg in ${this.house}.${this.id}`, msg);
		msg.setHouseId(this.house);
		msg.setDoorId(this.id);
		this.house.onMsg(msg, this);
		if (House.DEBUG) console.groupEnd();
	}

	receiveRequest<R extends IReply>(msg: IRequest<R>): Promise<R> {
		msg.setHouseId(this.house);
		msg.setDoorId(this.id);
		return this.house.onRequest(msg, this);
	}

	toString() {return "PublicDoor " + this.house + "." + this.id}
}

interface IPromiseMem<R extends IReply> {
	_ack: R,
	_resolve: (m: R) => void,
	_reject: (m: R) => void
}

/**
 * Extrémité d'une road connectée à une door.
 */
export abstract class RoadEnd {
	public door: Door;
	protected _counter = 0;
	protected _pendingMsg: Map<number, IPromiseMem<IReply>>;

	/** Envoie un msg. */
	pushMsg(msg: Msg) {
		this.xPushMsg(msg);
	}

	/** Envoie une requete, un Promise[resolve(Reply), reject(Reply)] est retourné */
	pushRequest<R extends IReply>(msg: Msg): Promise<R> {
		msg = Object.create(msg); //clone dans le cas où msg est proxié, il faut préserver msg.repWId pour répondre à l'appelant.
		msg.repWId = ++this._counter;
		const memory = {} as IPromiseMem<R>;
		const promise = new Promise<R>((resolve, reject) => {
			if (memory._ack) {
				if (memory._ack.isOk) resolve(memory._ack); else reject(memory._ack);
			} else {
				memory._resolve = resolve;
				memory._reject = reject;
			}
		});
		if (!this._pendingMsg) this._pendingMsg = new Map();
		this._pendingMsg.set(msg.repWId, memory);
		this.xPushMsg(msg);
		return promise;
	}

	/**
	 * Implémentation de l'envoi.
	 */
	abstract xPushMsg(msg: Msg): void;

	/** Appelé par l'implémentation de la Road pour les messages issus de l'autre extrémité. */
	listenIncoming(msg: Msg) {
		if (msg instanceof ReplyMsg && this._pendingMsg) {
			//on traite une réponse (ack ou reply)
			const memory = this._pendingMsg.get(msg.forId);
			if (memory) {
				this._pendingMsg.delete(msg.forId);
				if (memory._resolve) {
					if (msg.isOk) memory._resolve(msg);
					else memory._reject(msg);
				} else {
					memory._ack = msg;
				}
				return;
			}
		}
		try {
			this.door.receiveMsg(msg);
		} catch (e) {
			console.error(e);
			this.pushMsg(new ErrorMsg().init(e, msg));
		}
	}

	/** Création d'une road pour la house pasée en paramètre. */
	newRoadFor(house: House, config?: ODoorConfig): RoadEnd {throw Error("not implemented");}

	/**
	 * La door associée a été fermée : l'autre extrémité de la road doit aussi fermer sa door.
	 * Attention à la récurisivté entre les fermetures des 2 extrémités.
	 */
	abstract doorClosed(): void;
}

/**
 * Messages circulant entre les houses.
 */
export abstract class Msg {

	/** impl pour IFactory<> (via prototype de Msg). */
	static buildFromJson(this: ObjectConstructor, json: any, factoryReg: FactoryRegistry<Msg>) {
		const obj = new this() as Msg;
		obj.fromJson(json, factoryReg);
		return obj;
	}

	/** impl pour IFactory<> (via prototype de Msg) */
	static buildFromStruct(this: ObjectConstructor, struct: any, factoryReg: FactoryRegistry<Msg>) {
		const obj = new this() as Msg;
		obj.fromStruct(struct, factoryReg);
		return obj;
	}

	/** House à l'origine de ce msg. Utilisé par les roads mutualisés. */
	houseId?: string;

	/** Id de la door par lequel ce msg est entré dans la house.
	 * Propriété propre à chaque house, donc non sérialisée.
	 */
	doorId: number;

	/**
	 * Id du Msg. Affecté par l'émetteur du msg pour suivi, log...
	 * Note : la gestion des request/response utilise un id dédié : repWId.
	 */
	id?: string;

	/** Id à utiliser pour retourner la réponse (Reply) d'un Msg envoyé sous forme de requête. */
	repWId?: number;

	/** Type de msg utilisé dans la sérialisation json / struct. */
	get type() {
		return (this.constructor as any).type;
	}

	/**
	 * Metas complémentaires sous forme d'un objet Json mobilisable par des traitements transversaux
	 * comme des fonctions d'audit (user / timestamp...) ou la gestion du undo/redo.
	 */
	metas?: Dict<any>;

	/**
	 * Affectation d'une meta au msg.
	 * Une meta dont le nom commence par un '$' est dite "stable" : elle est préservée
	 * lorsqu'un message de type undo/redo est créé à partir de son msg d'origine.
	 */
	setMeta(key: string, val: any): this {
		if (!this.metas) this.metas = Object.create(null);
		this.metas[key] = val;
		return this;
	}

	getMeta(key: string): any {return this.metas ? this.metas[key] : null}

	setDoorId(doorId: number): this {
		this.doorId = doorId;
		return this;
	}

	setHouseId(house: House): this {
		this.houseId = house.id;
		return this;
	}

	/**
	 * Clonage profond, ie sans dépendance, alternative plus sécurisée de Object.create(this);
	 */
	clone(purpose?: string): this {
		const clone = new (this.constructor as any)() as this;
		clone.cloneFrom(this, purpose);
		//clone.fromStruct(this.asStruct(), House.MSG_FACTORYREG);
		return clone;
	}

	/** Retourne une forme Json sérialisable de ce Msg. */
	asJson() {
		const obj = Object.create(null);
		this.toJson(obj);
		return obj;
	}

	/** Retourne une forme "Structured Object" de ce Msg compatible avec worker.postMesssage() */
	asStruct() {
		const obj = Object.create(null);
		this.toStruct(obj);
		return obj;
	}

	/** Construit un Json. Méthode interne, à surcharger. */
	toJson(json: any) {
		json.type = this.type;
		json.houseId = this.houseId;
		if (this.id) json.id = this.id;
		if (this.metas) json.metas = this.metas;
		if (this.repWId) json.repWId = this.repWId;
	}

	/** Init à partir d'un Json. Méthode interne, à surcharger. */
	fromJson(json: any, factoryReg: FactoryRegistry<Msg>) {
		this.houseId = json.houseId;
		if (json.id) this.id = json.id;
		if (json.metas) this.metas = json.metas;
		if (json.repWId) this.repWId = json.repWId;
	}

	protected cloneFrom(o: this, purpose?: string) {
		this.houseId = o.houseId;
		if (o.id) this.id = o.id;
		if (o.metas) this.metas = JSON.parse(JSON.stringify(o.metas)); //PERF TODO Object deep clone
		if (o.repWId) this.repWId = o.repWId;
	}

	/** Construit un Struct. Surchargeable en cas de différence avec Json. */
	toStruct(struct: any) {
		this.toJson(struct)
	}

	/** Init à partir d'un Struct. Surchargeable en cas de différence avec Json. */
	fromStruct(struct: any, factoryReg: FactoryRegistry<Msg>) {
		this.fromJson(struct, factoryReg)
	}

	toString(): string {return JSON.stringify(this.asJson())}
}

/**
 * Message d'erreur retourné à l'appelant en cas d'echec d'envoi d'un message.
 * Utilisé en retour pour les messages en mode simple, sans accuser réception ni request/reply
 * ou si la connexion est interrompue.
 * {
 * type : "error"
 * forMsg : {}
 * error : ...
 * }
 */
export class ErrorMsg extends Msg {
	static readonly type = "error";

	/** Erreur indiquant l'impossibilité de synchroniser deux houses. */
	static readonly OUT_OF_SYNC = "outOfSync";

	error: any;
	forMsg: Msg;

	init(error: any, forMsg?: Msg) {
		this.error = error;
		this.forMsg = forMsg;
		return this;
	}

	toJson(json: any) {
		super.toJson(json);
		json.forMsg = this.forMsg.asJson();
		json.error = this.error;
	}

	fromJson(json: any, factoryReg: FactoryRegistry<Msg>) {
		super.fromJson(json, factoryReg);
		if (json.forMsg) this.forMsg = factoryReg.newFromJson(json.forMsg);
		this.error = json.error;
	}

	protected cloneFrom(o: this, purpose?: string) {
		super.cloneFrom(o, purpose);
		if (o.forMsg) this.forMsg = o.forMsg.clone(purpose); //clone ? non plus vu comme un singleton ?
		this.error = o.error;
	}
}

House.MSG_FACTORYREG.register(ErrorMsg);

/**
 * Message de réponse d'une IRequest.
 * {
 * type : "ack"
 * forId : "msg.repWId"
 * error : ...
 * }
 */
export class ReplyMsg extends Msg implements IReply {
	/** Message de réponse générique par défaut (aknoledge), généralement pour une erreur inconnue. */
	static readonly type: string = "ack";

	forId: number;
	error: any;

	initAck(msg: Msg, error?: any): ReplyMsg {
		this.forId = msg.repWId;
		if (error) this.error = error;
		return this;
	}

	get isOk() {
		return !this.error
	}

	toJson(json: any) {
		super.toJson(json);
		json.forId = this.forId;
		if (this.error) json.error = this.error;
	}

	fromJson(json: any, factoryReg: FactoryRegistry<Msg>) {
		super.fromJson(json, factoryReg);
		this.forId = json.forId;
		if (json.error) this.error = json.error;
	}

	protected cloneFrom(o: this, purpose?: string) {
		super.cloneFrom(o, purpose);
		this.forId = o.forId;
		if (o.error) this.error = o.error;
	}
}

House.MSG_FACTORYREG.register(ReplyMsg);

/**
 * Request en attente d'une réponse.
 */
export interface IRequest<Rep> extends Msg {
}

/**
 * Classe à étendre pour construire les messages de réponse à une requête (ie un Msg envoyé via Door.sendRequest() ).
 * Cette réponse doit impérativement gérer les propriétés forId et error définies par AckMsg.
 * @see ReplyMsg
 *
 */
export interface IReply extends Msg {
	forId: number
	error: any
	isOk: boolean
}

/** Request de demande d'initialisation d'une slaveHouse à sa masterHouse. */
export class InitSlaveReq extends Msg implements IRequest<InitSlaveRep> {
	static readonly type = "initSlaveReq";
}

House.MSG_FACTORYREG.register(InitSlaveReq);

/**
 * Message de réponse pour l'initialisation d'une connection ou suite à un OutOfSync.
 * {
 * type : "InitSlaveRep"
 * forId : "msgId"
 * slaveId : cf House.newSlaveId()
 * datas : dépendant de chaque type de house.
 * }
 */
export class InitSlaveRep extends ReplyMsg {
	static readonly type: string = "initSlaveRep";

	slaveId: number;
	datas: any;

	init(msgReq: Msg, slaveId: number, datas: any): InitSlaveRep {
		this.forId = msgReq.repWId;
		this.slaveId = slaveId;
		this.datas = datas;
		return this;
	}

	toJson(json: any) {
		super.toJson(json);
		json.forId = this.forId;
		json.slaveId = this.slaveId;
		json.datas = this.datas;
	}

	fromJson(json: any, factoryReg: FactoryRegistry<Msg>) {
		super.fromJson(json, factoryReg);
		this.forId = json.forId;
		this.slaveId = json.slaveId;
		this.datas = json.datas;
	}

	protected cloneFrom(o: this, purpose?: string) {
		super.cloneFrom(o, purpose);
		this.forId = o.forId;
		this.slaveId = o.slaveId;
		this.datas = o.datas; //datas immutable ? JSON.parse(JSON.stringify(o.datas));
	}
}

House.MSG_FACTORYREG.register(InitSlaveRep);


/**
 * Mémoire d'un état stockable dans la house et sérialisable.
 */
export abstract class State {

	static buildFromJson(this: ObjectConstructor, json: any, factoryReg: FactoryRegistry<State>) {
		const obj = new this() as State;
		obj.fromJson(json, factoryReg);
		return obj;
	}

	static buildFromStruct(this: ObjectConstructor, struct: any, factoryReg: FactoryRegistry<State>) {
		const obj = new this() as State;
		obj.fromStruct(struct, factoryReg);
		return obj;
	}

	/** Type de State utilisé dans la sérialisation json / struct. */
	get type() {
		return (this.constructor as any).type
	}


	/**
	 * Id du State.
	 */
	id?: string;


	/**
	 * Metas complémentaires sous forme d'un objet Json mobilisable par des traitements transversaux
	 * comme des fonctions d'audit (user / timestamp...) ou la gestion du undo/redo.
	 */
	metas?: any;

	/**
	 *
	 */
	house: House;

	/**
	 * Un state attaché à une house peut ensuite proposer des méthodes de modifications
	 * qui seront répercutées sur la house via des Msg appropriés.
	 * Appelé avec null pour signifier que la house killed.
	 */
	attachHouse(house: House | null): this {
		this.house = house;
		return this;
	}

	/**
	 * Update de l'état par un Msg issu de la house.
	 * Cette méthode *DOIT* uniquement être appelé par la house.
	 * D'autres méthodes spécifiques à chaque state doivent être créées pour être appelées
	 * de l'extérieur qui transmet ensuite à la house via la publicDoor via un Msg :
	 * state.setX(x) --> this.house.publiDoor.receiveMsg(new XMsg().init(x)) --> state.update(msg)
	 */
	abstract update(msg: Msg): void;

	/**
	 * Clonage profond, ie sans dépendance, alternative plus sécurisée de Object.create(this);
	 */
	clone(): this {
		//PERF à surcharger pour éviter une Struct intermédiaire.
		const clone = new (this.constructor as any)() as this;
		//XXX Si on utilise des FactoryRegistry spécialisés, faudra mémoriser le factoryReg dans fromJson(json, factoryReg).
		clone.fromStruct(this.asStruct(), House.STATE_FACTORYREG);
		return clone;
	}


	/** Retourne une forme Json sérialisable de ce State. */
	asJson() {
		const obj = Object.create(null);
		this.toJson(obj);
		return obj;
	}

	/** Retourne une forme "Structured Object" de ce State compatible avec worker.postMesssage() */
	asStruct() {
		const obj = Object.create(null);
		this.toStruct(obj);
		return obj;
	}

	/** Construit un Json. Méthode interne, à surcharger. */
	toJson(json: any) {
		json.type = this.type;
		if (this.id) json.id = this.id;
		if (this.metas) json.metas = this.metas;
	}

	/** Init à partir d'un Json. Méthode interne, à surcharger. */
	fromJson(json: any, factoryReg: FactoryRegistry<State>) {
		if (json.id) this.id = json.id;
		if (json.metas) this.metas = json.metas;
	}

	/** Construit un Struct. Surchargeable en cas de différence avec Json. */
	toStruct(struct: any) {
		this.toJson(struct)
	}

	/** Init à partir d'un Struct. Surchargeable en cas de différence avec Json. */
	fromStruct(struct: any, factoryReg: FactoryRegistry<State>) {
		this.fromJson(struct, factoryReg)
	}

	toString() {
		return JSON.stringify(this.asJson())
	}
}

/**
 * Message de demande de reset d'état actant une rupture avec la progression actuelle.
 * Peut-être utilisé pour retransmettre une réponse de type InitSlaveRep à ses propres slaves.
 * {
 * type : "ResetStates"
 * datas : dépendant de chaque type de house, identique à InitSlaveRep.
 * }
 */
export class ResetStatesMsg extends Msg {
	static readonly type = "resetStates";

	datas: any;

	init(datas: any) {
		this.datas = datas;
		return this;
	}

	toJson(json: any) {
		super.toJson(json);
		json.datas = this.datas;
	}

	fromJson(json: any, factoryReg: FactoryRegistry<Msg>) {
		super.fromJson(json, factoryReg);
		this.datas = json.datas;
	}

	protected cloneFrom(o: this, purpose?: string) {
		super.cloneFrom(o, purpose);
		this.datas = o.datas; //datas logiquement immutable, pas de clone.
	}
}

House.MSG_FACTORYREG.register(ResetStatesMsg);


/**
 * Implémentation d'une extrémité d'une road locale, ie interne au contexte javascript courant.
 * La transmission du message est asycnhone, ie la house cible lira le message dans le cycle js suivant.
 */
export class RoadLocal extends RoadEnd {

	static connect(house1: House, configDoor1: ODoorConfig, house2: House, configDoor2: ODoorConfig): [Door, Door] {
		const r1 = new RoadLocal();
		const r2 = new RoadLocal();
		const d1 = house1.openDoor(r1, configDoor1);
		const d2 = house2.openDoor(r2, configDoor2);
		r1.initRoad(d1, r2);
		r2.initRoad(d2, r1);
		return [d1, d2];
	}

	static connectMasterSlave(houseMaster: House, configDoorMaster: ODoorConfig, houseSlave: House, configDoorSlave: ODoorConfig): [Door, Door] {
		if (!configDoorMaster) configDoorMaster = {};
		configDoorMaster.isMaster = true;
		if (!configDoorSlave) configDoorSlave = {};
		configDoorSlave.roadToMaster = true;
		const r1 = new RoadLocal();
		const r2 = new RoadLocal();
		const d1 = houseMaster.openDoor(r1, configDoorMaster);
		const d2 = houseSlave.openDoor(r2, configDoorSlave);
		r1.initRoad(d1, r2);
		r2.initRoad(d2, r1);
		return [d1, d2];
	}

	protected _otherEnd: RoadLocal;

	initRoad(door: Door, otherEnd: RoadLocal) {
		this.door = door;
		this._otherEnd = otherEnd;
	}

	xPushMsg(msg: Msg) {
		const msgClone = msg.clone();
		Promise.resolve().then(() => {
			this._otherEnd.listenIncoming(msgClone)
		});
	}

	doorClosed() {
		this.door = null;
		const otherDoor = this._otherEnd.door;
		if (otherDoor) otherDoor.house.closeDoor(otherDoor);
	}

}


/**
 * Implémentation synchrone d'une extrémité d'une road locale, ie interne au contexte javascript courant.
 * ATTENTION Peut-être plus simple pour suivre et debugger mais peut avoir des effets de bord complexes et des
 * différences de comportements avec une road asynchrone.
 */
export class RoadLocalSync extends RoadLocal {

	static connect(house1: House, configDoor1: ODoorConfig, house2: House, configDoor2: ODoorConfig): [Door, Door] {
		const r1 = new RoadLocalSync();
		const r2 = new RoadLocalSync();
		const d1 = house1.openDoor(r1, configDoor1);
		const d2 = house2.openDoor(r2, configDoor2);
		r1.initRoad(d1, r2);
		r2.initRoad(d2, r1);
		return [d1, d2];
	}

	static connectMasterSlave(houseMaster: House, configDoorMaster: ODoorConfig, houseSlave: House, configDoorSlave: ODoorConfig): [Door, Door] {
		if (!configDoorMaster) configDoorMaster = {};
		configDoorMaster.isMaster = true;
		if (!configDoorSlave) configDoorSlave = {};
		configDoorSlave.roadToMaster = true;
		const r1 = new RoadLocalSync();
		const r2 = new RoadLocalSync();
		const d1 = houseMaster.openDoor(r1, configDoorMaster);
		const d2 = houseSlave.openDoor(r2, configDoorSlave);
		r1.initRoad(d1, r2);
		r2.initRoad(d2, r1);
		return [d1, d2];
	}

	xPushMsg(msg: Msg) {
		this._otherEnd.listenIncoming(msg.clone())
	}

}

/** Définition d'un fournisseur de réseau compatible avec WebSocket (W3cClient ou require('ws').Server de NodeJS) ou RTCDataChannel.*/
export interface IRoadNetProvider {
	send(data: string): void;

	close(): void;

	onmessage(messgaEvent: MessageEvent): void;

	onopen(evt: Event): void;

	onerror(evt: Event): void;

	onclose(evt: CloseEvent): void;
}

export interface ODoorNetConfig extends ODoorConfig {
	/** function appelée après l'init de la connexion avec la door créée en 1er paramètre.*/
	onOpen?: (door: Door) => void;
	onClose?: (door: Door) => void;
}

/**
 * Implémentation d'une extrémité d'une road de type WebSocket (W3cClient ou require('ws').Server de NodeJS) ou RTCDataChannel.
 */
export class RoadNet extends RoadEnd {

	protected _provider: IRoadNetProvider;

	protected _config: ODoorNetConfig;

	/**
	 *
	 * @param provider Objet WebSocket ou RTCDataChannel qui possède les méthodes :
	 *  - send(in DOMString data);
	 *  - onmessage(in MessageEvent);
	 *  - onopen(in Event);
	 *  - onerror(in Event);
	 *  - onclose(in Event);
	 *
	 *  @param house House à connecter.
	 *  @param configDoor Configuration de la door.
	 */
	constructor(provider: IRoadNetProvider, house: House, configDoor: ODoorNetConfig) {
		super();
		this._provider = provider;
		this._config = configDoor;
		provider.onopen = (ev: Event) => {
			this.door = house.openDoor(this, configDoor);
			if (configDoor.onOpen) configDoor.onOpen(this.door);
		};
		provider.onerror = (ev: Event) => {
			//en cas de coupure : error dans firefox, pas dans chrome.
			//mais dans les 2 cas, onclose est appelé, donc on capte cet event.
			console.log("RoadNet error: ", ev);
			//this.listenIncoming(new ErrorMsg().init("RoadNet failed" + errorEvt.type));
		};
		provider.onclose = (ev: Event) => {
			//console.log("RoadNet closed");
			if (this.door) {
				if (configDoor.onClose) configDoor.onClose(this.door);
				house.closeDoor(this.door);
			}
		};
		provider.onmessage = (ev: MessageEvent) => {
			const json = JSON.parse(ev.data);
			this.listenIncoming(House.MSG_FACTORYREG.newFromJson(json));
		};
		//provider.onopen(null); //1er appel (pas appelé par le provider, utilisé pour les réouvertures après interruption ?)
	}

	xPushMsg(msg: Msg) {
		this._provider.send(JSON.stringify(msg.asJson()));
	}

	newRoadFor(house: House, config?: ODoorConfig): RoadEnd {
		return new RoadNet(this._provider, house, config || this._config);
	}

	doorClosed() {
		if (this._config.onClose) this._config.onClose(this.door);
		this.door = null;
		this._provider.close();
	}
}
